import { useState } from 'react';
import ModalContainer from '../shared/ModalContainer';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';

export default function CreateRoleModal({ isOpen, onClose, onRoleCreated }) {
  const [formData, setFormData] = useState({
    name: '',
    theme: 'blue'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const themes = [
    { value: 'purple', label: 'Purple', colorClass: 'bg-purple-500' },
    { value: 'blue', label: 'Blue', colorClass: 'bg-blue-500' },
    { value: 'green', label: 'Green', colorClass: 'bg-green-500' },
    { value: 'orange', label: 'Orange', colorClass: 'bg-orange-500' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await adminApi.roles.create({
        name: formData.name.trim(),
        theme: formData.theme
      });

      toast.success(`Role "${formData.name}" created successfully`);
      onRoleCreated?.(response);
      handleClose();
    } catch (error) {
      console.error('Failed to create role:', error);
      toast.error(error.message || 'Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', theme: 'blue' });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Role"
      size="md"
    >
      <div className="space-y-6">
        {/* Role Name */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Role Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-4 py-2 bg-gray-800 border ${
              errors.name ? 'border-red-500' : 'border-gray-700'
            } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500`}
            placeholder="Enter role name (e.g., Manager, Developer)"
            disabled={isSubmitting}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-400">{errors.name}</p>
          )}
        </div>

        {/* Theme Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Theme Color
          </label>
          <div className="grid grid-cols-4 gap-3">
            {themes.map(theme => (
              <button
                key={theme.value}
                type="button"
                onClick={() => handleChange('theme', theme.value)}
                disabled={isSubmitting}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                  formData.theme === theme.value
                    ? `border-${theme.value}-500 bg-${theme.value}-500/10`
                    : 'border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className={`w-8 h-8 rounded-full ${theme.colorClass}`} />
                <span className="text-xs text-gray-300">{theme.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isSubmitting ? 'Creating...' : 'Create Role'}
          </button>
        </div>
      </div>
    </ModalContainer>
  );
}
